import React, { useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    CardActions,
    Button,
    Avatar
} from '@mui/material';
import IndustryIcon from '@mui/icons-material/Business'; // Import an icon for Industry
import IndividualIcon from '@mui/icons-material/Person'; // Import an icon for Individual
import { useNavigate } from 'react-router-dom';

const UserSelection = () => {
    const [selectedType, setSelectedType] = useState(null);
    const navigate = useNavigate();

    const handleCardClick = (type) => {
        setSelectedType(type);
    };

    const handleCategorySelect = (category) => {
        if (selectedType === 'Industry') {
            switch (category) {
                case 'INDUSTRY':
                    navigate('/form/industry');
                    break;
                case 'INSTITUTION':
                    navigate('/form/institution');
                    break;
                case 'TRADE UNION':
                    navigate('/form/tradeunion');
                    break;
                case 'ASSOCIATION':
                    navigate('/form/association');
                    break;
                default:
                    alert('Invalid selection for Industry category.');
            }
        } else if (selectedType === 'Individual') {
            switch (category) {
                case 'INDIVIDUAL':
                    navigate('/form/indivual');
                    break;
                case 'LIFE MEMBER':
                    navigate('/form/lifemember');
                    break;
                case 'SENIOR CITIZEN':
                    navigate('/form/seniorcitizen');
                    break;
                case 'STUDENT':
                    navigate('/form/student');
                    break;
                default:
                    alert('Invalid selection for Individual category.');
            }
        } else {
            alert('Please select a valid type (Industry or Individual).');
        }
    };
    
    // Common card styling for consistent height and appearance
    const cardStyles = {
        height: '280px',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        }
    };

    const cardContentStyles = {
        textAlign: 'center',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '24px 16px'
    };

    const renderCategoryCards = () => {
        const categories = selectedType === 'Industry' 
            ? ['INDUSTRY', 'INSTITUTION', 'TRADE UNION', 'ASSOCIATION'] 
            : ['INDIVIDUAL', 'LIFE MEMBER', 'SENIOR CITIZEN', 'STUDENT'];

        return categories.map((category) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={category}>
                <Card sx={cardStyles}>
                    <CardContent sx={cardContentStyles}>
                        <Avatar sx={{ 
                            bgcolor: selectedType === 'Industry' ? 'primary.main' : 'secondary.main', 
                            margin: 'auto', 
                            width: 56, 
                            height: 56,
                            mb: 2
                        }}>
                            {selectedType === 'Industry' ? <IndustryIcon /> : <IndividualIcon />}
                        </Avatar>
                        <Typography 
                            variant="h6" 
                            component="div" 
                            sx={{ 
                                marginBottom: 1,
                                fontWeight: 600,
                                minHeight: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {category}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                                lineHeight: 1.4,
                                minHeight: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {`Register as a ${category.toLowerCase()} member to access specific resources.`}
                        </Typography>
                    </CardContent>
                    <CardActions sx={{ 
                        justifyContent: 'center',
                        padding: '16px'
                    }}>
                        <Button 
                            size="medium" 
                            variant="contained" 
                            color={selectedType === 'Industry' ? 'primary' : 'secondary'}
                            onClick={() => handleCategorySelect(category)}
                            sx={{
                                minWidth: '100px',
                                fontWeight: 600
                            }}
                        >
                            Select
                        </Button>
                    </CardActions>
                </Card>
            </Grid>
        ));
    };

    return (
        <Box sx={{ 
            flexGrow: 1, 
            padding: { xs: '16px', sm: '20px', md: '24px' },
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {selectedType === null ? (
                <Box sx={{ width: '100%', maxWidth: '800px' }}>
                    <Typography 
                        variant="h4" 
                        component="h1" 
                        sx={{ 
                            textAlign: 'center', 
                            mb: 4,
                            fontWeight: 700,
                            color: 'primary.main'
                        }}
                    >
                        Choose Your Registration Type
                    </Typography>
                    <Grid container spacing={4} justifyContent="center">
                        {/* Industry Card */}
                        <Grid item xs={12} sm={6} md={5}>
                            <Card 
                                onClick={() => handleCardClick('Industry')} 
                                sx={{
                                    ...cardStyles,
                                    height: '320px'
                                }}
                            >
                                <CardContent sx={cardContentStyles}>
                                    <Avatar sx={{ 
                                        bgcolor: 'primary.main', 
                                        margin: 'auto', 
                                        width: 64, 
                                        height: 64,
                                        mb: 2
                                    }}>
                                        <IndustryIcon sx={{ fontSize: 32 }} />
                                    </Avatar>
                                    <Typography 
                                        variant="h5" 
                                        component="div" 
                                        sx={{ 
                                            marginBottom: 2,
                                            fontWeight: 700,
                                            color: 'primary.main'
                                        }}
                                    >
                                        INDUSTRY
                                    </Typography>
                                    <Typography 
                                        variant="body1" 
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.5,
                                            textAlign: 'center'
                                        }}
                                    >
                                        Register as an industry member to access exclusive resources and networking opportunities.
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{ 
                                    justifyContent: 'center',
                                    padding: '16px'
                                }}>
                                    <Button 
                                        size="large" 
                                        variant="contained" 
                                        color="primary"
                                        sx={{
                                            minWidth: '120px',
                                            fontWeight: 600
                                        }}
                                    >
                                        Select
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>

                        {/* Individual Card */}
                        <Grid item xs={12} sm={6} md={5}>
                            <Card 
                                onClick={() => handleCardClick('Individual')} 
                                sx={{
                                    ...cardStyles,
                                    height: '320px'
                                }}
                            >
                                <CardContent sx={cardContentStyles}>
                                    <Avatar sx={{ 
                                        bgcolor: 'secondary.main', 
                                        margin: 'auto', 
                                        width: 64, 
                                        height: 64,
                                        mb: 2
                                    }}>
                                        <IndividualIcon sx={{ fontSize: 32 }} />
                                    </Avatar>
                                    <Typography 
                                        variant="h5" 
                                        component="div" 
                                        sx={{ 
                                            marginBottom: 2,
                                            fontWeight: 700,
                                            color: 'secondary.main'
                                        }}
                                    >
                                        INDIVIDUAL
                                    </Typography>
                                    <Typography 
                                        variant="body1" 
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.5,
                                            textAlign: 'center'
                                        }}
                                    >
                                        Register as an individual member to join our community and participate in events.
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{ 
                                    justifyContent: 'center',
                                    padding: '16px'
                                }}>
                                    <Button 
                                        size="large" 
                                        variant="contained" 
                                        color="secondary"
                                        sx={{
                                            minWidth: '120px',
                                            fontWeight: 600
                                        }}
                                    >
                                        Select
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                // Render additional category cards based on the selected type
                <Box sx={{ width: '100%', maxWidth: '1200px' }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: 4,
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        <Typography 
                            variant="h4" 
                            component="h1" 
                            sx={{ 
                                fontWeight: 700,
                                color: selectedType === 'Industry' ? 'primary.main' : 'secondary.main'
                            }}
                        >
                            Select {selectedType} Category
                        </Typography>
                        <Button 
                            variant="outlined" 
                            onClick={() => setSelectedType(null)}
                            sx={{
                                fontWeight: 600
                            }}
                        >
                            Back to Selection
                        </Button>
                    </Box>
                    <Grid container spacing={3} justifyContent="center">
                        {renderCategoryCards()}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default UserSelection;